import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "./http"
import { keys } from "./keys"
import {
	NO_CATALOG,
	PRINTED_CATALOGS,
	PUBLISH_SITES,
	RIGA_PRICE_GROUP,
	RUSSIAN_LANGUAGE,
	SITE_API_ENABLED,
	WEB_CATALOG_GROUP,
} from "./config"
import type {
	Attribute,
	Catalog,
	CatalogGroupRow,
	Guide,
	GroupCategory,
	GroupProductHit,
	GroupSiteStatus,
	ImageType,
	Language,
	NamedImage,
	NotReadyToDeployGroup,
	Pictogram,
	PriceGroup,
	ProductGroup,
	ProductGroupCatalog,
	ProductGroupIdentity,
	ProductIdentity,
	ProductRow,
	ToolsetCandidate,
	ToolsetProduct,
	TranslateRow,
	TranslateSource,
	TranslateWithUrl,
	WebCategory,
	WebsiteIdentity,
} from "./types"
import { WebsiteSchema } from "./types"

/**
 * Чтение из ecm. Один хук — один запрос; отбор, поиск и подсчёты живут на
 * экранах, потому что наборы приходят целиком и лежат в кэше.
 *
 * Общие умолчания заданы в `main.tsx`: `retry: 1`, без перезапроса по возврату
 * во вкладку. Здесь перечислено только то, что от них отличается.
 */

export type Membership = "own" | "foreign" | "none"

/** Принадлежность группы — выводится из PriceGroupID.
 *  Соответствует GroupMembership в GroupMembershipReader.cs:56-63. */
export function membershipOf(priceGroupId: number | null): Membership {
	if (priceGroupId === RIGA_PRICE_GROUP) return "own"
	if (priceGroupId === null || priceGroupId === 0) return "none"
	return "foreign"
}

/* ─── группы ────────────────────────────────────────────────── */

/** Список групп. Одним запросом приходит весь набор (≈665 КБ, 24 мс на стенде),
 *  дальше фильтрация и поиск идут на клиенте — мгновенно и без дёрганья сервера. */
export function useGroups(languageId: number) {
	return useQuery({
		queryKey: keys.groups(languageId),
		staleTime: 5 * 60 * 1000,
		queryFn: () =>
			api.get<ProductGroupIdentity[]>("/api/product-group/identity", {
				languageId,
				searchString: "",
				checkType: 0,
				groupFilter: 0,
			}),
	})
}

/**
 * Поиск групп по товарам внутри них: артикул и название товара. Отвечает не
 * `ecm`, а рижский BFF (`GET /ai/groups/by-product`) — в `ecm` поиск групп идёт
 * только по коду и названию самой группы, а российский контур мы не меняем.
 *
 * Запрос уходит с задержкой и от двух символов (`useDebounced` на экране,
 * `MinQueryLength` на узле). Пока летит новый ответ, экран держит прошлый
 * (`placeholderData`): иначе строки, найденные по товару, мигали бы на каждую
 * букву.
 */
export function useGroupsByProduct(query: string, languageId: number) {
	const needle = query.trim()
	return useQuery({
		queryKey: keys.groupsByProduct(needle, languageId),
		enabled: SITE_API_ENABLED && needle.length >= 2,
		staleTime: 5 * 60 * 1000,
		placeholderData: previous => previous,
		queryFn: () =>
			api.get<GroupProductHit[]>("/ai/groups/by-product", { q: needle, languageId }),
	})
}

export function useGroup(id: string | undefined, languageId: number) {
	return useQuery({
		queryKey: keys.group(id, languageId),
		enabled: !!id,
		// get-or-reserve — единственный способ получить карточку группы;
		// он же ставит мьютекс редактирования (см. BeaconController)
		queryFn: () =>
			api.post<ProductGroup>("/api/product-group/get-or-reserve", {
				languageId,
				productGroupId: id,
				productsIds: null,
			}),
	})
}

/** Резервирование идентификатора под новую группу.
 *  Тот же get-or-reserve, но без productGroupId: ERP выдаёт следующий свободный
 *  номер и держит его в памяти процесса до создания (ProductGroupIdReserver).
 *  Ключ без языка и staleTime: Infinity — иначе каждый повтор резервирует новый номер. */
export function useReservedGroupId() {
	return useQuery({
		queryKey: keys.reservedGroupId(),
		staleTime: Infinity,
		gcTime: Infinity,
		retry: false,
		queryFn: () =>
			api.post<ProductGroup>("/api/product-group/get-or-reserve", {
				languageId: RUSSIAN_LANGUAGE,
				productGroupId: "",
				productsIds: null,
			}),
	})
}

/** Табличная часть: товары группы вместе со значениями атрибутов.
 *  Этот же запрос дописывает товарам недостающие атрибуты группы
 *  (ProductAttributeManager.ProductAttributesReplenishment). */
export function useGroupRows(id: string | undefined, languageId: number) {
	return useQuery({
		queryKey: keys.groupRows(id, languageId),
		enabled: !!id,
		queryFn: async () => {
			const rows = await api.get<ProductRow[]>(
				"/api/product/products-with-attr/by-groups",
				{ productGroupId: id!, languageId }
			)
			return [...rows].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
		},
	})
}

/** Товары без группы — кандидаты на добавление в карточку.
 *  priceGroupId фиксирует набор: у непустой группы это её прайс-группа,
 *  у пустой — рижская (CompositionTab.tsx:53-75 старого фронта). */
export function useFreeProducts(
	priceGroupId: number | null,
	languageId: number,
	enabled: boolean
) {
	return useQuery({
		queryKey: keys.freeProducts(priceGroupId, languageId),
		enabled,
		staleTime: 5 * 60 * 1000,
		queryFn: () =>
			api.get<ProductIdentity[]>("/api/product/identity/no-group", {
				priceGroupId,
				languageId,
				statisticFilter: 0,
			}),
	})
}

export function useGroupImages(id: string | undefined) {
	return useQuery({
		queryKey: keys.groupImages(id),
		enabled: !!id,
		staleTime: 10 * 60 * 1000,
		queryFn: () => api.get<NamedImage[]>("/api/images", { productGroupId: id! }),
	})
}

/**
 * Пиктограммы группы — значки свойств товара; на сайте это `oc_product_option`,
 * в печатном каталоге — иконки под карточкой.
 *
 * Названия приходят на языке запроса, поэтому ключ кэша с языком.
 */
export function useGroupPictograms(id: string | undefined, languageId: number) {
	return useQuery({
		queryKey: keys.groupPictograms(id, languageId),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
		queryFn: () =>
			api.get<Pictogram[]>("/api/pictograms", {
				productGroupId: id!,
				languageId,
			}),
	})
}

/**
 * Состав набора (комплектация). Есть только у групп-наборов (`isToolset`), и
 * только пока у карточки уже есть свой товар — `toolsetId` это его `id`
 * (`ToolsetReader.cs` требует, чтобы он принадлежал переданной группе).
 *
 * Роли те же, что на чтение табличной части: RF, Lv, Rg, Photographer,
 * Translator — просмотр открыт всем, а вот запись `ToolsetControllerAuthHelper`
 * делит по принадлежности группы: в латвийских наборах пишут Lv и Rg, в
 * российских — только RF. Значит РФ-наборы Рига здесь только смотрит.
 */
export function useToolsetProducts(
	groupId: string | undefined,
	toolsetId: string | undefined,
	languageId: number
) {
	return useQuery({
		queryKey: keys.toolsetProducts(groupId, toolsetId, languageId),
		enabled: !!groupId && !!toolsetId,
		queryFn: async () => {
			const rows = await api.get<ToolsetProduct[]>("/api/toolsets", {
				productGroupId: groupId!,
				toolsetId: toolsetId!,
				languageId,
			})
			return [...rows].sort((a, b) => a.sort - b.sort)
		},
	})
}

/** Кандидаты на добавление в состав — весь справочник товаров ERP за вычетом
 *  уже добавленных и самого набора. Список не фильтруется прайс-группой на
 *  бэке, поэтому запрашиваем только когда панель состава открыта на запись —
 *  иначе на РФ-наборах, где панель всё равно только читает, это лишний тяжёлый
 *  запрос. */
export function usePossibleToolsetProducts(
	groupId: string | undefined,
	toolsetId: string | undefined,
	languageId: number,
	enabled: boolean
) {
	return useQuery({
		queryKey: keys.possibleToolsetProducts(groupId, toolsetId, languageId),
		enabled: enabled && !!groupId && !!toolsetId,
		staleTime: 60 * 1000,
		queryFn: () =>
			api.get<ToolsetCandidate[]>("/api/toolsets/get-possible-toolset-products", {
				productGroupId: groupId!,
				toolsetId: toolsetId!,
				languageId,
			}),
	})
}

/* ─── справочники ───────────────────────────────────────────── */

/** Весь справочник пиктограмм — 68 штук, из него их и выбирают. */
export function usePictograms(languageId: number) {
	return useQuery({
		queryKey: keys.pictograms(languageId),
		staleTime: 30 * 60 * 1000,
		queryFn: () => api.get<Pictogram[]>("/api/pictograms/all", { languageId }),
	})
}

/** Справочник атрибутов — из него набираются колонки таблицы. */
export function useAttributes(languageId: number) {
	return useQuery({
		queryKey: keys.attributes(languageId),
		staleTime: Infinity,
		queryFn: () => api.get<Attribute[]>("/api/attributes", { languageId }),
	})
}

export function useImageTypes() {
	return useQuery({
		queryKey: keys.imageTypes(),
		staleTime: Infinity,
		queryFn: () => api.get<ImageType[]>("/api/images/types"),
	})
}

export function useLanguages() {
	return useQuery({
		queryKey: keys.languages(),
		staleTime: Infinity,
		queryFn: () => api.get<Language[]>("/api/translate/lang"),
	})
}

export function usePriceGroups() {
	return useQuery({
		queryKey: keys.priceGroups(),
		staleTime: Infinity,
		queryFn: () => api.get<PriceGroup[]>("/api/price-group"),
	})
}

/** Каталоги: печатные, сайты и каталог продуктов — одним списком, как их
 *  отдаёт ERP. Разделение на группы — в справочнике переводов. */
export function useCatalogs() {
	return useQuery({
		queryKey: keys.catalogs(),
		staleTime: Infinity,
		queryFn: () => api.get<Catalog[]>("/api/catalogs"),
	})
}

/**
 * Сайты. Полный ответ лежит в кэше под одним ключом, а отбор идёт в `select`:
 * так второй отбор видит список целиком, а не остатки после первого.
 *
 * Источник — рижский узел (`/ai/site/websites`), не `ecm`
 * (`GET /api/websites/identity`). С 26 августа `WebSites.IsActive` в проде
 * значит «скрыт из ecm» (пользователь сам выключил его у всех 12 латвийских
 * сайтов отдельной сессией, не как «сайт не работает») — `ecm` эти сайты
 * принципиально не покажет ни при каких условиях, значит и опираться на его
 * список для латвийских сайтов больше нельзя. `/ai/site/websites` читает
 * `WebSites` напрямую из ERP без этого фильтра (`SiteDirectory.GetWebsitesAsync`).
 * Тот же предохранитель, что у остальных `/ai/*` — без `RIGA_AI_ORIGIN`
 * локально запрос не уйдёт вовсе.
 */
export function useWebsites<T = WebsiteIdentity[]>(
	select?: (all: WebsiteIdentity[]) => T
) {
	return useQuery({
		queryKey: keys.websites(),
		enabled: SITE_API_ENABLED,
		staleTime: Infinity,
		select,
		queryFn: () => api.get<WebsiteIdentity[]>("/ai/site/websites"),
	})
}

// отборы объявлены снаружи хука: react-query пересчитывает `select` при смене
// его ссылки, а стрелка в теле хука новая на каждом рендере
const latvianOnly = (all: WebsiteIdentity[]) =>
	all.filter(site => site.schema === WebsiteSchema.Latvian)

const publishOnly = (all: WebsiteIdentity[]) =>
	all.filter(site => PUBLISH_SITES.includes(site.name))

/** Сайты Риги — только `Schema = 2`, 11 не-российских доменов.
 *  Нужны справочнику переводов для ссылок на товар. */
export function useLatvianWebsites() {
	return useWebsites(latvianOnly)
}

/** Сайты, на которые разрешено грузить, — короткий список `PUBLISH_SITES`. */
export function usePublishWebsites() {
	return useWebsites(publishOnly)
}

/**
 * Печатные каталоги, заведённые Ригой через форму создания на `/publish`
 * (`CreateSiteOrCatalog.tsx`). Своей колонки «это каталог Риги» у `Catalogs`
 * нет — узел держит список на диске, тем же приёмом, что и журнал публикации
 * (`CatalogRegistry.cs`).
 */
export function usePrintedCatalogIds() {
	return useQuery({
		queryKey: keys.printedCatalogIds(),
		// `/ai/*` — рижский узел; локально без `RIGA_AI_ORIGIN` там нет даже
		// прокси (`vite.config.ts`), запрос уходит в SPA-fallback самого vite
		// (html вместо json) — тот же предохранитель, что у `useSiteStatus`.
		enabled: SITE_API_ENABLED,
		staleTime: 60 * 1000,
		queryFn: () => api.get<number[]>("/ai/catalog/printed-ids"),
	})
}

/**
 * Каталоги, вообще имеющие смысл на рижском экране «Каталоги»: свои сайтовые
 * (имя совпадает с латвийским `WebSites` — так же их находит и публикация,
 * `SiteDirectory.GetSiteCatalogIdAsync`) и печатные — статический список
 * `PRINTED_CATALOGS` плюс заведённые через форму создания
 * (`usePrintedCatalogIds`). Остальные ~20 каталогов (российские, каталог
 * продуктов) сюда не попадают — их Риге видеть незачем.
 *
 * Новый сайт создаётся сразу со `Schema = 2`, поэтому его каталог появляется
 * в списке без правки конфига; новый печатный каталог — как только узел
 * дописал его в свой реестр.
 */
export function useRigaCatalogs() {
	const catalogs = useCatalogs()
	const sites = useLatvianWebsites()
	const printedIds = usePrintedCatalogIds()

	const data = useMemo(() => {
		if (!catalogs.data) return undefined
		const siteNames = new Set((sites.data ?? []).map(site => site.name))
		const printed = new Set(printedIds.data ?? [])
		return catalogs.data.filter(
			catalog =>
				siteNames.has(catalog.name) ||
				(catalog.isPrinted &&
					(PRINTED_CATALOGS.includes(catalog.name) || printed.has(catalog.id)))
		)
	}, [catalogs.data, sites.data, printedIds.data])

	return {
		data,
		isLoading: catalogs.isLoading || sites.isLoading || printedIds.isLoading,
	}
}

/** Состав каталога — ответ рижского BFF (`/ai/catalog/groups`), не `ecm`:
 *  обратного метода «группы по каталогу» Риге в ecm не даёт роль
 *  (`GET /api/product-group/identity/by-catalog` — RF, Supply). Название
 *  группы приходит на `languageId`, вывод — максимум по строкам каталога. */
export function useCatalogGroups(catalogId: number | null, languageId: number) {
	return useQuery({
		queryKey: keys.catalogGroups(catalogId ?? undefined, languageId),
		// Тот же предохранитель, что у `usePrintedCatalogIds`/`useSiteStatus` —
		// без него на стенде без `RIGA_AI_ORIGIN` запрос молча получает html
		// SPA-fallback вместо json (см. `http.ts#request` и находку с белым
		// экраном на «Каталоги и сайты», 26 августа).
		enabled: SITE_API_ENABLED && catalogId !== null,
		staleTime: 60 * 1000,
		retry: false,
		queryFn: () =>
			api.get<CatalogGroupRow[]>("/ai/catalog/groups", {
				catalogId: catalogId!,
				languageId,
			}),
	})
}

/**
 * Данные генерации каталога на одном языке — `GET /api/product-group/guide`.
 * Используется страницей `/pdf` дважды: сначала как проверка охвата перевода
 * (по языку — доля групп и товаров с непустым названием, `pdf/coverage.ts`),
 * затем как источник данных для самого документа — второй раз не запрашивает,
 * ответ уже в кэше react-query под тем же ключом.
 *
 * Запрос тяжёлый (полный обход каталога плюс сырой SQL на бэке,
 * `GroupReader.cs:324-421`) и результат почти не меняется в течение дня,
 * поэтому кэш держим 10 минут. Сайт обязателен — без него бэк не строит
 * QR-ссылки и падает на `!site.IsActive`.
 */
export function useGuide(
	catalogId: number | null,
	websiteId: number | null,
	languageId: number,
	enabled = true
) {
	return useQuery({
		queryKey: keys.guide(catalogId, websiteId, languageId),
		enabled: enabled && catalogId !== null && websiteId !== null,
		staleTime: 10 * 60 * 1000,
		gcTime: 20 * 60 * 1000,
		retry: false,
		queryFn: () => fetchGuide(catalogId!, websiteId!, languageId),
	})
}

/** Тот же запрос без хука — язык генерации каталога перебирается на экране
 *  `/pdf` вне рендера (`useQueries` для охвата, `queryClient.fetchQuery` для
 *  самой генерации, `screens/pdf/`), а не через `useGuide` в цикле.
 *
 * `skipSiteUrl: true` — просьба к `ecm` не резолвить `urlQr` группы самому
 * (внутренний поход в MySQL сайта из RU, ≈15 с таймаут для сайтов вроде
 * латвийского, недоступных из RU быстро). Рига резолвит те же ссылки сама,
 * с рижского узла (`groupUrls.ts` → `GroupUrlService.cs`, доли секунды) и
 * накладывает их поверх `guide.data[].groups[].urlQr` перед сборкой PDF —
 * то, что вернул бы `ecm` без этого флага, всё равно тут же перезаписывается
 * (см. `screens/pdf/groupUrls.ts#applyGroupUrls`). Параметр форвард-совместимый:
 * пока `ecm` его не понимает (см. `ЗАДАЧА-ECM-PDF-GUIDE.md`), ASP.NET-биндинг
 * молча игнорирует незнакомое поле запроса — поведение не меняется, 15 с
 * никуда не деваются; как только `ecm` выкатит поддержку флага, эффект
 * появится сам, без отдельного релиза Риги. */
export function fetchGuide(catalogId: number, websiteId: number, languageId: number) {
	return api.get<Guide>("/api/product-group/guide", { catalogId, websiteId, languageId, skipSiteUrl: true })
}

/* ─── категории ─────────────────────────────────────────────── */

/** Дерево категорий сайта целиком: 589 узлов на проде, приходит одним ответом
 *  уже вложенным. Названия зависят от языка, поэтому язык — часть ключа. */
export function useWebCategories(languageId: number) {
	return useQuery({
		queryKey: keys.webCategories(languageId),
		staleTime: Infinity,
		queryFn: () =>
			api.get<WebCategory[]>("/api/category", {
				catalogGroup: WEB_CATALOG_GROUP,
				languageId,
			}),
	})
}

/** Категории группы на сайте. Бэк отдаёт только пути, названия берутся из дерева.
 *  Категории из другого веб-дерева отсеиваются там же: путь до чужого корня
 *  не строится, и запись выпадает (`GroupCategoryManager.cs:61-66`). */
export function useGroupCategories(id: string | undefined, languageId: number) {
	return useQuery({
		queryKey: keys.groupCategories(id, languageId),
		enabled: !!id,
		queryFn: () =>
			api.get<GroupCategory[]>("/api/category/product-group-cats", {
				productGroupId: id!,
				catalogGroup: WEB_CATALOG_GROUP,
				languageId,
			}),
	})
}

/* ─── переводы ──────────────────────────────────────────────── */

/**
 * Справочник переводов: весь набор по паре «каталог + раздел» на выбранном языке.
 *
 * Отбор пустых, поиск и подсчёты делаются на клиенте, а не запросом. Так и
 * правильнее, и быстрее: `isEmpty` бэк применяет уже после выборки
 * (`TranslateReader.cs:51-52`), то есть тяжёлый запрос отрабатывает всё равно
 * целиком, а поиск на сервере вдобавок выкидывает из строки все знаки, кроме
 * букв, цифр, дефиса и палки (`FixSearchString`).
 *
 * Замеры на локальном стенде (латышский): `www.avtodelo.lv` целиком — 13 720
 * строк, 3,9 МБ, 2,8 с; «Каталог Автодело 2025» — 5 583 строки, 1,2 с;
 * «Вне каталогов» — 791 строка, 0,3 с. Один такой запрос на смену фильтра
 * вместо запроса на каждую букву поиска.
 */
export function useTranslates(
	catalogId: number | null,
	source: TranslateSource,
	languageId: number
) {
	return useQuery({
		queryKey: keys.translates(catalogId, source, languageId),
		// каталог выбирается после загрузки списка каталогов
		enabled: catalogId !== null,
		staleTime: 5 * 60 * 1000,
		gcTime: 15 * 60 * 1000,
		retry: false,
		queryFn: () =>
			api.get<TranslateRow[]>("/api/translate", {
				// «Вне каталогов» — параметр не отправляем совсем
				catalogId: catalogId === NO_CATALOG ? undefined : catalogId,
				translateSource: source,
				targetLanguageId: languageId,
				search: "",
				isEmpty: false,
				isSemantic: false,
				semanticSearch: "",
			}),
	})
}

/**
 * Ссылки на товар на сайте для строк справочника.
 *
 * Единственное, что нельзя посчитать на клиенте: адрес собирается из категорий
 * и алиасов OpenCart в базе самого сайта (`UrlGroupProvider`), а до неё достаёт
 * только бэк. Символы, наоборот, считаем у себя — правило простое и известное.
 *
 * Запрос дорогой: `with-urls` гоняет ту же выборку, что и весь справочник
 * (`GetAllTranslates`), плюс лезет в MySQL сайта. Поэтому он выключен, пока
 * колонку не включили, и держится в кэше десять минут. Роли — только `Lv` и
 * `Rg` (`TranslateController.cs:87`), у `Translator` будет 403.
 *
 * Возвращаем сразу карту «sourceId → адрес»: ссылка привязана к группе, а не
 * к строке перевода, и в наборе одна и та же группа встречается многократно.
 */
export function useTranslateUrls(
	catalogId: number | null,
	websiteId: number | null,
	languageId: number,
	enabled: boolean
) {
	return useQuery({
		queryKey: keys.translateUrls(catalogId, websiteId, languageId),
		// вне каталогов бэку нечего сопоставлять: каталог сайта обязателен
		enabled:
			enabled &&
			catalogId !== null &&
			catalogId !== NO_CATALOG &&
			websiteId !== null,
		staleTime: 10 * 60 * 1000,
		gcTime: 15 * 60 * 1000,
		retry: false,
		queryFn: async () => {
			const rows = await api.get<TranslateWithUrl[]>(
				"/api/translate/with-urls",
				{
					catalogId,
					targetLanguageId: languageId,
					websiteId,
					isEmpty: false,
					search: "",
				}
			)
			const urls = new Map<string, string>()
			for (const row of rows) if (row.url) urls.set(row.sourceId, row.url)
			return urls
		},
	})
}

/* ─── публикация ────────────────────────────────────────────── */

/**
 * Каталоги группы: строка на каждую пару «каталог + категория» со статусом
 * «выводить». Единственный способ узнать, лежит ли группа в каталоге сайта и
 * прописан ли ей вывод — без первого `add-group` отвечает `404`, без второго
 * `422` («Группа не имеет нужный статус отображения»,
 * `SiteProductService.cs:268-270`). Вывод интерфейс проставляет сам при
 * загрузке, спрашивать о нём человека Рига не должна.
 *
 * Карточку группы (`get-or-reserve`) для этого дёргать нельзя: она ставит
 * мьютекс редактирования, а на экране загрузки группу только смотрят.
 */
export function useGroupCatalogs(productGroupId: string | null) {
	return useQuery({
		queryKey: keys.groupCatalogs(productGroupId ?? undefined),
		enabled: !!productGroupId,
		staleTime: 60 * 1000,
		retry: false,
		queryFn: () => fetchGroupCatalogs(productGroupId!),
	})
}

/** Тот же запрос без хука — для очередей, где группы перебираются по одной
 *  (починка вывода на `/publish`). */
export function fetchGroupCatalogs(productGroupId: string) {
	return api.get<ProductGroupCatalog[]>("/api/catalogs/group-catalogs", {
		productGroupId,
	})
}

/**
 * Состояние группы на сайте: есть ли она там, сколько её строк лежит в каждой
 * таблице и что мешает выгрузке.
 *
 * Спрашиваем **рижский BFF**, а не `ecm`: в базы сайтов Риги ходит только
 * рижский узел (`SITE_API_ENABLED`). Российский `is-on-site` из интерфейса
 * убран совсем — он открывает соединение с MySQL сайта из России, и на
 * неудаче роняет процесс `ecm` целиком.
 *
 * BFF отвечает за доли секунды и падений не вызывает, поэтому запрос ходит
 * сам, как любой другой: ни кнопки-предохранителя, ни памяти о том, «отвечала
 * ли база в этой вкладке», больше не нужно.
 */
export function useSiteStatus(
	productGroupId: string | null,
	websiteId: number | null,
	enabled = true
) {
	return useQuery({
		queryKey: keys.siteStatus(productGroupId, websiteId),
		enabled:
			enabled && SITE_API_ENABLED && !!productGroupId && websiteId !== null,
		staleTime: 30 * 1000,
		retry: false,
		queryFn: () =>
			api.get<GroupSiteStatus>("/ai/site/group-status", {
				groupId: productGroupId!,
				websiteId: websiteId!,
			}),
	})
}

/**
 * Группы каталога, которые массовая загрузка на сайт пропустит.
 *
 * Бэк поднимает все группы каталога и весь состав каталога сайта
 * (`DeployChecker.cs:35-75`); старый фронт давал запросу таймаут 30 минут, но на
 * стенде `www.avtodelo.lt` он отвечает за 0,8 с — 2 934 строки, 440 КБ. Ответ
 * всё равно крупный, поэтому запускаем по кнопке и держим в кэше.
 *
 * Причин осталось две: не прописанный вывод в каталоге (2 927 из 2 934) и
 * непроверенные изображения (7); проверки переводов в бэке закомментированы.
 * Первую рижский интерфейс чинит сам — кнопкой «Прописать вывод всем».
 */
export function useNotReadyToDeploy(
	catalogId: number | null,
	websiteId: number | null,
	enabled: boolean
) {
	return useQuery({
		queryKey: keys.notReadyToDeploy(catalogId, websiteId),
		enabled: enabled && catalogId !== null && websiteId !== null,
		staleTime: 5 * 60 * 1000,
		gcTime: 15 * 60 * 1000,
		retry: false,
		queryFn: () =>
			api.get<NotReadyToDeployGroup[]>("/api/websites/not-ready-to-deploy", {
				catalogId: catalogId!,
				websiteId: websiteId!,
			}),
	})
}
